import { useEffect, useState } from "react";

import type { ColumnsConfigType } from "app/pages/sales-intelligence/modules/enrichment/store/type";
import type {
    ConfigField,
    CsvExportColumnCategory,
} from "app/pages/sales-intelligence/modules/enrichment/types/columns";
import type { EnrichmentType } from "app/pages/sales-intelligence/modules/enrichment/types/types";
import type { LeadsColumnCategoryDto } from "app/pages/sales-intelligence/modules/leads-search";

const EMPTY_LEADS_FIELDS: Readonly<Partial<Record<LeadsColumnCategoryDto, readonly ConfigField[]>>> = {
    companyAndWebsite: [],
    incomingTraffic: [],
    technologies: [],
    visitorsAndEngagement: [],
    marketingChannels: [],
    apps: [],
    adSpend: [],
};

const EMPTY_CONTACTS_FIELDS: Readonly<Partial<Record<CsvExportColumnCategory, readonly ConfigField[]>>> = {
    contacts: [],
    companyAndWebsite: [],
};

export const useSelectedConfigFields = (config: readonly ColumnsConfigType[], type: EnrichmentType = "company") => {
    const [selectedConfigFields, setSelectedConfigFields] = useState<
        Readonly<Partial<Record<CsvExportColumnCategory, readonly ConfigField[]>>>
    >(type === "company" ? EMPTY_LEADS_FIELDS : EMPTY_CONTACTS_FIELDS);

    const setSelectedConfigByName = (name: CsvExportColumnCategory, payload: readonly ConfigField[]) => {
        setSelectedConfigFields((prevState) => ({
            ...prevState,
            [name]: [...payload],
        }));
    };

    const setAllConfigs = (allConfigs: Record<CsvExportColumnCategory, readonly ConfigField[]>) => {
        setSelectedConfigFields(allConfigs);
    };

    const checkSelectedFieldsAreInConfig = (configToCheck: readonly ColumnsConfigType[]) => {
        if (configToCheck.length === 0) {
            return;
        }

        setSelectedConfigFields((prevState) => {
            const newState = { ...prevState };

            configToCheck.forEach(({ name, fields }) => {
                const fieldNamesFromConfig = new Set(fields?.map(({ field }) => field?.name) ?? []);

                newState[name] = prevState[name]?.filter((field) => fieldNamesFromConfig.has(field.name));
            });

            return newState;
        });
    };

    useEffect(() => {
        checkSelectedFieldsAreInConfig(config);
    }, [config]);

    return { selectedConfigFields, setSelectedConfigByName, setAllConfigs };
};
